// Document + mermaid import flows (and the manual flow-text edit).
// They replace the session's flow with one inferred server-side and
// use the same conflict shape (409 + existingStatementCount) when the
// session already has content.
//
// On success, both re-read the session blob and update newModel +
// discoveryFeatures via setters injected by the orchestrator — that
// keeps state single-sourced in useSession instead of duplicating the
// load path here.

import { useCallback, useState, type MutableRefObject } from 'react';
import type { NewModel } from '../utils/flowModel';
import type {
  DiscoveryFeaturesPayload,
  DocImportResult,
  SessionData,
} from './sessionTypes';
import { coerceNewModel, coerceDiscoveryFeatures } from '../utils/sessionCoercers';

export interface UseImportsOptions {
  sessionIdRef: MutableRefObject<string | null>;
  setNewModel: (m: NewModel) => void;
  setDiscoveryFeatures: (p: DiscoveryFeaturesPayload) => void;
}

export interface UseImportsReturn {
  importFromDocument: (
    documentText: string,
    opts?: { filename?: string; force?: boolean },
  ) => Promise<DocImportResult>;
  isImportingDocument: boolean;
  importFromMermaid: (
    args: { mermaid?: string; flowText?: string; force?: boolean },
  ) => Promise<DocImportResult>;
  isImportingMermaid: boolean;
  // Manual flow-grammar edit — replaces newModel.flowText verbatim.
  // Same conflict shape as the other importers.
  setFlowText: (
    args: { flowText: string; force?: boolean },
  ) => Promise<DocImportResult>;
  isSavingFlowText: boolean;
}

export function useImports(opts: UseImportsOptions): UseImportsReturn {
  const { sessionIdRef, setNewModel, setDiscoveryFeatures } = opts;

  const [isImportingDocument, setIsImportingDocument] = useState(false);
  const [isImportingMermaid, setIsImportingMermaid] = useState(false);
  const [isSavingFlowText, setIsSavingFlowText] = useState(false);

  // After a successful import the server has rewritten newModel and
  // scheduled feature extraction. Pull the fresh state so the canvas
  // re-renders and the features panel flips to 'pending' until the
  // worker finishes. Failures here are non-fatal — log and move on.
  const refreshAfterImport = useCallback(async (id: string, label: string) => {
    try {
      const sRes = await fetch(`/api/sessions/${id}`);
      if (sRes.ok) {
        const session: SessionData = await sRes.json();
        if (session.newModel) setNewModel(coerceNewModel(session.newModel));
        setDiscoveryFeatures(coerceDiscoveryFeatures(session.discoveryFeatures, 'pending'));
      }
    } catch (err) {
      console.warn(`[${label}] post-import session refresh failed:`, err);
    }
  }, [setNewModel, setDiscoveryFeatures]);

  // ── Document import ───────────────────────────────────────────
  // Replaces the session's flow with one assembled from a structured
  // markdown table or CSV. 409 = destructive overwrite confirmation needed.
  const importFromDocument = useCallback(async (
    documentText: string,
    opts: { filename?: string; force?: boolean } = {},
  ): Promise<DocImportResult> => {
    const id = sessionIdRef.current;
    if (!id) return { ok: false, error: 'No active session' };
    if (!documentText?.trim()) return { ok: false, error: 'Document text is required' };
    setIsImportingDocument(true);
    try {
      const res = await fetch(`/api/sessions/${id}/import-from-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentText,
          filename: opts.filename || '',
          force: opts.force === true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return {
          ok: false,
          status: res.status,
          error: data.error || `HTTP ${res.status}`,
          conflict: data.conflict,
        };
      }
      await refreshAfterImport(id, 'DocImport');
      return { ok: true, payload: data };
    } catch (err) {
      console.error('[DocImport] failed:', err);
      return { ok: false, error: (err as Error).message };
    } finally {
      setIsImportingDocument(false);
    }
  }, [sessionIdRef, refreshAfterImport]);

  // ── Mermaid import ────────────────────────────────────────────
  // Accepts either raw `mermaid` (server will convert) or a pre-
  // converted `flowText` (the modal's editable preview). Same conflict
  // shape as the other importers — 409 with existingStatementCount.
  const importFromMermaid = useCallback(async (
    args: { mermaid?: string; flowText?: string; force?: boolean },
  ): Promise<DocImportResult> => {
    const id = sessionIdRef.current;
    if (!id) return { ok: false, error: 'No active session' };
    if (!args.mermaid?.trim() && !args.flowText?.trim()) {
      return { ok: false, error: 'mermaid or flowText is required' };
    }
    setIsImportingMermaid(true);
    try {
      const res = await fetch(`/api/sessions/${id}/import-from-mermaid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mermaid: args.mermaid || '',
          flowText: args.flowText || '',
          force: args.force === true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return {
          ok: false,
          status: res.status,
          error: data.error || `HTTP ${res.status}`,
          conflict: data.conflict,
        };
      }
      await refreshAfterImport(id, 'MermaidImport');
      return { ok: true, payload: data };
    } catch (err) {
      console.error('[MermaidImport] failed:', err);
      return { ok: false, error: (err as Error).message };
    } finally {
      setIsImportingMermaid(false);
    }
  }, [sessionIdRef, refreshAfterImport]);

  // ── Manual flow-text edit ────────────────────────────────────
  // Posts the operator's edited grammar straight to the session blob.
  // Same conflict shape (409 + existingStatementCount) as the other
  // import paths so the modal can reuse the destructive-overwrite UI.
  const setFlowText = useCallback(async (
    args: { flowText: string; force?: boolean },
  ): Promise<DocImportResult> => {
    const id = sessionIdRef.current;
    if (!id) return { ok: false, error: 'No active session' };
    setIsSavingFlowText(true);
    try {
      const res = await fetch(`/api/sessions/${id}/flow-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flowText: args.flowText,
          force: args.force === true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return {
          ok: false,
          status: res.status,
          error: data.error || `HTTP ${res.status}`,
          conflict: data.conflict,
        };
      }
      await refreshAfterImport(id, 'FlowTextEdit');
      return { ok: true, payload: data };
    } catch (err) {
      console.error('[FlowText] save failed:', err);
      return { ok: false, error: (err as Error).message };
    } finally {
      setIsSavingFlowText(false);
    }
  }, [sessionIdRef, refreshAfterImport]);

  return {
    importFromDocument,
    isImportingDocument,
    importFromMermaid,
    isImportingMermaid,
    setFlowText,
    isSavingFlowText,
  };
}
